import * as XLSX from "xlsx";

export interface ParsedSpreadsheet {
  headers: string[];
  rows: Record<string, string>[];
  sheetName: string;
  totalRows: number;
}

const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8 MB
const MAX_ROWS = 500;
const MAX_COLUMNS = 64;

/**
 * Đọc file Excel (.xlsx, .xls) hoặc CSV sang mảng objects với đầy đủ giới hạn an toàn
 */
export async function parseSpreadsheetFile(file: File): Promise<ParsedSpreadsheet> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("Tệp bảng tính vượt quá dung lượng cho phép (tối đa 8 MB).");
  }

  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, {
    type: "array",
    cellDates: true,
    cellFormula: false,
    cellHTML: false,
    raw: false,
  });

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error("Tệp không chứa trang tính (sheet) nào.");
  }

  const worksheet = workbook.Sheets[firstSheetName];
  if (!worksheet) {
    throw new Error("Không thể đọc trang tính.");
  }

  const rawRows: (string | number | boolean | null | undefined)[][] = XLSX.utils.sheet_to_json(
    worksheet,
    { header: 1, defval: "" },
  );

  if (!rawRows || rawRows.length === 0) {
    return { headers: [], rows: [], sheetName: firstSheetName, totalRows: 0 };
  }

  // Tìm dòng tiêu đề đầu tiên không rỗng
  let headerRowIndex = 0;
  while (
    headerRowIndex < rawRows.length &&
    (!rawRows[headerRowIndex] ||
      rawRows[headerRowIndex]!.filter((c) => String(c).trim().length > 0).length === 0)
  ) {
    headerRowIndex += 1;
  }

  if (headerRowIndex >= rawRows.length) {
    return { headers: [], rows: [], sheetName: firstSheetName, totalRows: 0 };
  }

  const rawHeaders = (rawRows[headerRowIndex] ?? []).map((c) =>
    String(c ?? "").trim(),
  );
  const headerColumns = rawHeaders
    .map((header, columnIndex) => ({ header, columnIndex }))
    .filter((item) => item.header.length > 0);

  if (headerColumns.length > MAX_COLUMNS) {
    throw new Error(
      `Tệp vượt quá giới hạn cột cho phép (tối đa ${MAX_COLUMNS} cột).`,
    );
  }

  const duplicateHeaders = headerColumns
    .map((item) => item.header.toLocaleLowerCase("vi"))
    .filter((header, index, all) => all.indexOf(header) !== index);
  if (duplicateHeaders.length > 0) {
    throw new Error(
      `Tiêu đề cột bị trùng: ${[...new Set(duplicateHeaders)].join(", ")}. Hãy đặt tên cột duy nhất trước khi nhập.`,
    );
  }

  const headers = headerColumns.map((item) => item.header);
  const rows: Record<string, string>[] = [];
  for (let i = headerRowIndex + 1; i < rawRows.length; i += 1) {
    const row = rawRows[i];
    if (!row || row.every((c) => String(c ?? "").trim().length === 0)) continue;

    if (rows.length >= MAX_ROWS) {
      throw new Error(`Mỗi lần nhập tối đa ${MAX_ROWS} dòng. Hãy tách tệp thành nhiều phần.`);
    }

    const rowObj: Record<string, string> = {};
    headerColumns.forEach(({ header, columnIndex }) => {
      const val = row[columnIndex];
      const strVal =
        val !== undefined && val !== null ? String(val).trim() : "";
      rowObj[header] = strVal;
    });

    rows.push(rowObj);
  }

  return {
    headers,
    rows,
    sheetName: firstSheetName,
    totalRows: rows.length,
  };
}

/**
 * Xuất dữ liệu sang file Excel (.xlsx) và kích hoạt tải về trên trình duyệt
 */
export function exportToExcel(
  filename: string,
  sheets: { sheetName: string; data: Record<string, unknown>[] }[],
): void {
  const workbook = XLSX.utils.book_new();

  for (const sheet of sheets) {
    const worksheet = XLSX.utils.json_to_sheet(sheet.data);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.sheetName.slice(0, 31));
  }

  XLSX.writeFile(workbook, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

// Từ điển nhận diện cột tiếng Việt + tiếng Anh thông dụng
export const COLUMN_SYNONYMS: Record<string, string[]> = {
  sku: [
    "sku",
    "mã sku",
    "mã hàng",
    "mã sản phẩm",
    "mã sp",
    "item code",
    "product code",
    "code",
  ],
  name: [
    "name",
    "tên hàng",
    "tên sản phẩm",
    "tên sp",
    "sản phẩm",
    "mặt hàng",
    "diễn giải",
    "item name",
    "description",
  ],
  businessVertical: [
    "businessvertical",
    "business vertical",
    "ngành kinh doanh",
    "ngành hàng",
    "lĩnh vực kinh doanh",
    "vertical",
  ],
  category: [
    "category",
    "danh mục",
    "nhóm hàng",
    "loại hàng",
    "product category",
  ],
  subcategory: [
    "subcategory",
    "sub category",
    "danh mục con",
    "nhóm hàng con",
    "loại hàng con",
    "product subcategory",
  ],
  barcode: [
    "barcode",
    "mã vạch",
    "mã vạch sp",
    "bar code",
    "upc",
    "ean",
  ],
  baseUnit: [
    "baseunit",
    "đvt",
    "đơn vị",
    "đơn vị tính",
    "đv",
    "unit",
    "uom",
  ],
  salePrice: [
    "saleprice",
    "giá bán",
    "đơn giá bán",
    "giá niêm yết",
    "giá lẻ",
    "price",
    "retail price",
  ],
  cost: [
    "cost",
    "unitcost",
    "openingcost",
    "giá nhập",
    "giá vốn",
    "đơn giá nhập",
    "đơn giá",
    "giá mua",
    "unit price",
    "purchase price",
  ],
  quantity: [
    "quantity",
    "openingqty",
    "số lượng",
    "sl",
    "sl nhập",
    "số lượng nhập",
    "tồn kho",
    "qty",
    "amount",
  ],
  lineTotal: [
    "linetotal",
    "thành tiền",
    "tổng tiền",
    "tổng",
    "cộng tiền",
    "total",
    "subtotal",
  ],
  lotCode: [
    "lotcode",
    "openinglotcode",
    "số lô",
    "lô",
    "mã lô",
    "lot",
    "batch",
  ],
  expiryDate: [
    "expirydate",
    "openingexpirydate",
    "hạn sử dụng",
    "hsd",
    "hạn dùng",
    "ngày hết hạn",
    "exp",
    "expiry",
  ],
  minStockQty: [
    "minstockqty",
    "tồn tối thiểu",
    "tồn min",
    "định mức tồn",
  ],
  trackExpiry: [
    "trackexpiry",
    "theo dõi hsd",
    "có hạn dùng",
    "quản lý hạn",
  ],
  unitsJson: [
    "unitsjson",
    "units json",
    "đơn vị quy đổi",
    "quy cách quy đổi",
    "quy cách bán",
    "conversion units",
  ],
  supplierName: [
    "suppliername",
    "nhà cung cấp",
    "ncc",
    "nhà phân phối",
    "đơn vị giao",
    "supplier",
    "vendor",
  ],
  customerName: [
    "customername",
    "khách hàng",
    "tên khách",
    "người mua",
    "customer",
  ],
  phone: [
    "phone",
    "số điện thoại",
    "sđt",
    "điện thoại",
    "hotline",
    "tel",
  ],
  address: [
    "address",
    "địa chỉ",
    "địa chỉ giao",
    "nơi nhận",
  ],
  taxCode: [
    "taxcode",
    "mã số thuế",
    "mst",
    "tax",
  ],
  email: [
    "email",
    "e-mail",
    "mail",
  ],
  legalName: [
    "legalname",
    "tên pháp lý",
    "tên công ty",
    "tên trên hóa đơn",
  ],
  bankName: [
    "bankname",
    "tên ngân hàng",
    "ngân hàng",
    "bank",
  ],
  bankAccountNumber: [
    "bankaccountnumber",
    "số tài khoản",
    "stk",
    "stk ngân hàng",
    "bank account",
    "account number",
  ],
  representativeName: [
    "representativename",
    "người đại diện",
    "người liên hệ",
    "đại diện liên hệ",
    "contact person",
  ],
  representativePhone: [
    "representativephone",
    "sđt người đại diện",
    "số điện thoại người đại diện",
    "sđt người liên hệ",
    "contact phone",
  ],
  note: [
    "note",
    "ghi chú",
    "diễn giải thêm",
    "mô tả",
    "remarks",
  ],
};

/**
 * Tự động đoán cột khớp nhất dựa trên tên header
 */
export function autoDetectField(header: string): string | null {
  const normalized = header
    .trim()
    .toLowerCase()
    .replace(/[_\-.:]/g, " ")
    .replace(/\s+/g, " ");

  // Chỉ tự động map khi header khớp chính xác một alias đã biết.
  // Không fuzzy-match bằng substring: ví dụ "unitsJson" không được phép
  // bị hiểu nhầm thành "unit/baseUnit". Trường không chắc chắn phải để
  // người dùng chọn thủ công thay vì đoán sai dữ liệu.
  for (const [field, synonyms] of Object.entries(COLUMN_SYNONYMS)) {
    if (synonyms.some((synonym) => normalized === synonym)) {
      return field;
    }
  }

  return null;
}

export type TemplateType = "GOODS_RECEIPT" | "PRODUCTS" | "SUPPLIERS" | "CUSTOMERS";

/**
 * Tạo và tải xuống file mẫu Excel cho từng loại dữ liệu
 */
export function downloadTemplateExcel(type: TemplateType): void {
  let filename = "";
  let sheetName = "";
  let data: Record<string, unknown>[] = [];

  switch (type) {
    case "GOODS_RECEIPT":
      filename = "BLed_Mau_Nhap_Hang.xlsx";
      sheetName = "Phiếu nhập hàng";
      data = [
        {
          "Mã SKU": "SP001",
          "Tên sản phẩm": "Coca Cola 330ml",
          "ĐVT": "lon",
          "Số lượng": 24,
          "Giá nhập": 8500,
          "Thành tiền": 204000,
          "Số lô": "L240901",
          "Hạn sử dụng": "2027-09-01",
          "Mã vạch": "8935001234567",
        },
        {
          "Mã SKU": "SP002",
          "Tên sản phẩm": "Mì Hảo Hảo tôm chua cay",
          "ĐVT": "gói",
          "Số lượng": 30,
          "Giá nhập": 3200,
          "Thành tiền": 96000,
          "Số lô": "L240905",
          "Hạn sử dụng": "2027-03-05",
          "Mã vạch": "8935007890123",
        },
        {
          "Mã SKU": "SP003",
          "Tên sản phẩm": "Dầu ăn Tường An 1L",
          "ĐVT": "chai",
          "Số lượng": 12,
          "Giá nhập": 42000,
          "Thành tiền": 504000,
          "Số lô": "",
          "Hạn sử dụng": "",
          "Mã vạch": "8935003344556",
        },
      ];
      break;

    case "PRODUCTS":
      filename = "BLed_Mau_Danh_Muc_San_Pham.xlsx";
      sheetName = "Sản phẩm";
      data = [
        {
          "Mã SKU": "SP001",
          "Tên sản phẩm": "Coca Cola 330ml",
          "Ngành kinh doanh": "Tạp hóa & Bách hóa",
          "Danh mục": "Đồ uống",
          "Danh mục con": "Nước ngọt có ga",
          "Mã vạch": "8935001234567",
          "ĐVT cơ bản": "lon",
          "Giá bán": 10000,
          "Tồn tối thiểu": 12,
          "Theo dõi HSD": "Có",
          "Đơn vị quy đổi (JSON)":
            '[{"name":"lốc","multiplier":6,"salePrice":58000},{"name":"thùng","multiplier":24,"salePrice":230000}]',
        },
        {
          "Mã SKU": "SP002",
          "Tên sản phẩm": "Mì Hảo Hảo tôm chua cay",
          "Ngành kinh doanh": "Tạp hóa & Bách hóa",
          "Danh mục": "Thực phẩm",
          "Danh mục con": "Mì ăn liền",
          "Mã vạch": "8935007890123",
          "ĐVT cơ bản": "gói",
          "Giá bán": 4000,
          "Tồn tối thiểu": 10,
          "Theo dõi HSD": "Có",
          "Đơn vị quy đổi (JSON)": '[{"name":"thùng","multiplier":30,"salePrice":115000}]',
        },
        {
          "Mã SKU": "SP003",
          "Tên sản phẩm": "Khăn giấy ướt Baby Care",
          "Ngành kinh doanh": "Tạp hóa & Bách hóa",
          "Danh mục": "Hóa mỹ phẩm & Chăm sóc",
          "Danh mục con": "Khăn giấy ướt",
          "Mã vạch": "8935009988776",
          "ĐVT cơ bản": "gói",
          "Giá bán": 25000,
          "Tồn tối thiểu": 5,
          "Theo dõi HSD": "Không",
          "Đơn vị quy đổi (JSON)": "",
        },
      ];
      break;

    case "SUPPLIERS":
      filename = "BLed_Mau_Nha_Cung_Cap.xlsx";
      sheetName = "Nhà cung cấp";
      data = [
        {
          "Tên nhà cung cấp": "Công ty Nước Giải Khát Tân Hiệp Hưng",
          "Tên pháp lý": "CÔNG TY TNHH NƯỚC GIẢI KHÁT TÂN HIỆP HƯNG",
          "Số điện thoại": "0903123456",
          "Email": "kinhdoanh@tanhiephung.vn",
          "Địa chỉ": "123 Đường Số 5, Phường An Phú, TP. Thủ Đức",
          "Mã số thuế": "0312345678",
          "Tên ngân hàng": "Vietcombank",
          "Số tài khoản": "0123456789",
          "Người đại diện": "Nguyễn Văn An",
          "SĐT người đại diện": "0912345678",
          "Ghi chú": "Giao hàng thứ 3 và thứ 6 hàng tuần",
        },
        {
          "Tên nhà cung cấp": "Đại lý Bánh kẹo & Nông sản Minh Phát",
          "Tên pháp lý": "",
          "Số điện thoại": "0918765432",
          "Email": "minhphat@example.vn",
          "Địa chỉ": "45 Chợ Đầu Mối Bình Điền, Quận 8, TP.HCM",
          "Mã số thuế": "0387654321",
          "Tên ngân hàng": "ACB",
          "Số tài khoản": "2233445566",
          "Người đại diện": "Trần Minh Phát",
          "SĐT người đại diện": "0918765432",
          "Ghi chú": "Thanh toán công nợ gối đầu 15 ngày",
        },
      ];
      break;

    case "CUSTOMERS":
      filename = "BLed_Mau_Khach_Hang.xlsx";
      sheetName = "Khách hàng";
      data = [
        {
          "Tên khách hàng": "Chị Hoa Tạp Hóa Hẻm 24",
          "Số điện thoại": "0977889900",
          "Địa chỉ": "24/8 Nguyễn Văn Cừ, Quận 5",
          "Mã số thuế": "",
          "Ghi chú": "Khách quen lấy sỉ, cho ghi nợ tối đa 5 triệu",
        },
        {
          "Tên khách hàng": "Anh Bình Quán Cà Phê Mộc",
          "Số điện thoại": "0933221100",
          "Địa chỉ": "108 Phan Xích Long, Phú Nhuận",
          "Mã số thuế": "8329482710",
          "Ghi chú": "Lấy sữa tươi và cà phê hạt hàng tuần",
        },
      ];
      break;
  }

  exportToExcel(filename, [{ sheetName, data }]);
}
